import { faAdd, faMinus, faPlus, faSearch, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import React, { forwardRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { TypesResponse } from '../../../../Redux/Api/Request/Types';
import { ModalContext } from '../../Modal/Modal';
import ScrollGradientContainer from '../../ScrollGradientContainer';

export interface CardTypeSelectionListProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    orgTypes: TypesResponse | undefined;
    types: string[];
    newType: string;
    setNewType: (value: string) => void;
    createNewType: () => void;
    removeType: (type: string) => void;
    setTypes: (types: string[]) => void;
    makeUnique: (types: string[]) => string[];
    onDelete: (id: string) => void;
}

const CardTypeSelectionList = forwardRef<HTMLDivElement, CardTypeSelectionListProps>((props) => {

    const { orgTypes, types, newType, setNewType, createNewType, removeType, setTypes, makeUnique, onDelete, ...restProps } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const modalContext = React.useContext(ModalContext);

    const filteredTypes = orgTypes?.filter(type =>
        type.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const createTypeDeleteBody = (type: { name: string, usageCount: number }) => {
        if (type.usageCount > 1) {
            return (t('cards:edit:typeDeletePrompt', { usageCount: type.usageCount, typeName: type.name }));
        } else if (type.usageCount === 1) {
            return (t('cards:edit:typeSingularUsageDeletePrompt', { typeName: type.name }));
        }
        return (t('cards:edit:typeNoUsageDeletePrompt', { typeName: type.name }));
    }

    return (
        <div className={twMerge(`border-[1px] border-[#e0e0e0] bg-white rounded-[0.4rem] overflow-hidden max-h-[304px]`, restProps.className)} >
            <div
                onClick={() => { document.getElementById('type-search')?.focus(); }}
                className="cursor-text border-b border-[#e0e0e0] flex items-center px-3 py-2 gap-3"
            >
                <FontAwesomeIcon icon={faSearch} className="text-gray-500 text-lg" />
                <input
                    id="type-search"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder={t('search') as string}
                    className="focus:outline-none w-full text-md py-1"
                />
            </div>

            <ScrollGradientContainer scrollBarClass="min-h-64 max-h-64" fadeClass="h-8">
                <div
                    onClick={() => { document.getElementById('new-type')?.focus(); }}
                    className='
                        px-3 py-2 text-[#5B6C79] text-md cursor-text
                        hover:bg-gray-100 transition-colors
                        overflow-hidden text-ellipsis whitespace-nowrap
                        border-b border-[#e0e0e0] flex items-center justify-between
                    '>
                    <input
                        id="new-type"
                        type="text"
                        value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                        placeholder={t('cards:list:createNewType') as string}
                        className="w-full focus:outline-none bg-transparent [&:has(+button:hover)]:text-blue-700"
                        maxLength={75}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                createNewType();
                            }
                        }}
                    />
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation; createNewType(); }}
                        className="hover:text-blue-700 disabled:text-gray-400"
                        disabled={!newType}
                    >
                        <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                    </button>
                </div>
                {filteredTypes?.map((type, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`
                            text-[#5B6C79] text-md cursor-pointer 
                            hover:text-blue-700 hover:bg-gray-100 transition-colors
                            border-b border-[#e0e0e0]
                            w-full
                            flex items-center
                        `}
                        onClick={() => {
                            if (types.includes(type.name)) {
                                removeType(type.name);
                            } else {
                                setTypes(makeUnique([...types, type.name]));
                            }
                        }}
                    >
                        <span className="pl-3 py-2 flex items-center min-w-0 flex-grow [&:has(+button:hover)]:text-red-700">
                            <FontAwesomeIcon
                                icon={types.includes(type.name) ? faMinus : faAdd}
                                className="w-4 h-4 mr-2 flex-shrink-0"
                            />
                            <span className="whitespace-nowrap overflow-hidden text-ellipsis selection:bg-blue-50">
                                {type.name}
                            </span>
                        </span>
                        <button
                            className='group px-3 py-2 flex-shrink-0'
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                modalContext.withModal({
                                    title: t("areYouSure"),
                                    body: createTypeDeleteBody({ name: type.name, usageCount: type.usageCount || 0 }),
                                }, () => {
                                    onDelete(type.id);
                                })();
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faTrashAlt}
                                className="w-4 h-4 text-[#5B6C79] group-hover:text-red-700"
                            />
                        </button>
                    </button>
                ))}
            </ScrollGradientContainer>
        </div>
    );
});

export default CardTypeSelectionList;